import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { DropdownButton, Dropdown } from "react-bootstrap";
import { API_URL } from "../../constants/Globals";

import {
  getSequenceAdvertisingType,
  getSequenceById,
  getSequenceTimeToNextPoll,
  updateSequenceById,
} from "../../services/CollectionsSvc";
import {
  numberOfQuestionsArray,
  questionTimerMapping,
} from "../../constants/Globals";

const SequenceSettings = ({ selNetworkId, sequenceId, onSave }) => {
  const [type, setType] = useState("Survey");
  const [timeToNextPoll, setTimeToNextPoll] = useState("None");
  const [advertising, setAdvertising] = useState("Off");
  const [questionCategory, setQuestionCategory] = useState([]);
  const [numberOfQuestions, setNumberOfQuestions] = useState(1);
  const [questionTimer, setQuestionTimer] = useState("Off");
  const [questionOrder, setQuestionOrder] = useState("In Order");
  const [showCorrectAnswers, setShowCorrectAnswers] = useState("Immediately");
  const [categories, setCategories] = useState([]);
  const [timeToNextPollOptions, setTimeToNextPollOptions] = useState([]);
  const [advertisingOptions, setAdvertisingOptions] = useState([]);
  const [sequence, setSequence] = useState(null);

  useEffect(() => {
    const fetchSequenceData = async () => {
      try {
        const response = await getSequenceById(sequenceId);
        const sequenceData = response.data.data;

        if (sequenceData) {
          setSequence(sequenceData);
          setType(sequenceData.type || "Survey");
          setTimeToNextPoll(sequenceData.timeToNextPoll?.name || "None");
          setAdvertising(sequenceData.advertisingType?.name || "Off");
          const populatedCategories =
            sequenceData.sequenceCategories?.map(
              (sequenceCategory) => sequenceCategory.category?.text
            ) || [];
          setQuestionCategory(populatedCategories);
          setQuestionTimer(
            Object.keys(questionTimerMapping).find(
              (key) => questionTimerMapping[key] === sequenceData.questionTimer
            ) || "Off"
          );
          setQuestionOrder(sequenceData.questionOrder || "In Order");
          setShowCorrectAnswers(
            sequenceData.showCorrectAnswers || "Immediately"
          );
          setNumberOfQuestions(sequenceData.numberOfQuestions ?? 1);
        }
      } catch (error) {
        console.error("Failed to fetch sequence data:", error);
      }
    };

    fetchSequenceData();
  }, [sequenceId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/categories?clientId=${selNetworkId}`
        );
        setCategories(response.data.data || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    if (type === "Quiz" && selNetworkId) {
      fetchCategories();
    }
  }, [type, selNetworkId]);

  useEffect(() => {
    const fetchTimeToNextPollOptions = async () => {
      try {
        const response = await getSequenceTimeToNextPoll();
        setTimeToNextPollOptions(response.data.data || []);
      } catch (error) {
        console.error("Failed to fetch Time to Next Poll options:", error);
      }
    };

    const fetchAdvertisingOptions = async () => {
      try {
        const response = await getSequenceAdvertisingType();
        setAdvertisingOptions(response.data.data || []);
      } catch (error) {
        console.error("Failed to fetch Advertising options:", error);
      }
    };

    fetchTimeToNextPollOptions();
    fetchAdvertisingOptions();
  }, [selNetworkId]);

  const dropdownStyle = {
    minWidth: "20%",
    maxWidth: "fit-content",
  };

  const handleSave = useCallback(async () => {
    let payload;
    if (type === "Survey") {
      payload = {
        name: sequence.name,
        clientId: selNetworkId,
        type: "Survey",
        timeToNextPoll:
          timeToNextPollOptions.find(
            (option) => option.name === timeToNextPoll
          ) || null,
        advertisingType:
          advertisingOptions.find((option) => option.name === advertising) ||
          null,
      };
    } else if (type === "Quiz") {
      const selectedCategories = categories
        .filter((category) => questionCategory.includes(category.text))
        .map((category) => ({ category }));
      const haveCategories = selectedCategories.length > 0;
      payload = {
        clientId: selNetworkId,
        name: sequence.name,
        type: "Quiz",
        sequenceCategories: haveCategories ? selectedCategories : [],
        advertisingType:
          advertisingOptions.find((option) => option.name === advertising) ||
          null,
        questionTimer: questionTimerMapping[questionTimer] || 0,
        questionOrder,
        showCorrectAnswers,
      };
      if (haveCategories) {
        payload.numberOfQuestions = parseInt(numberOfQuestions);
      }
    }

    updateSequenceById(sequenceId, payload)
      .then(() => {
        alert("Sequence saved successfully!");
      })
      .catch((error) => {
        console.error("Failed to save sequence:", error);
        alert("Failed to save sequence. Please try again.");
      });

    if (onSave) {
      onSave(payload);
    }
  }, [
    type,
    sequence,
    selNetworkId,
    timeToNextPollOptions,
    timeToNextPoll,
    numberOfQuestions,
    advertisingOptions,
    advertising,
    categories,
    questionCategory,
    questionTimer,
    questionOrder,
    showCorrectAnswers,
    sequenceId,
    onSave,
  ]);

  useEffect(() => {
    if (onSave) {
      onSave(handleSave);
    }
  }, [onSave, handleSave]);

  return (
    <div
      className="settings-tab-box"
      style={{
        borderRadius: "5px",
        padding: "1rem",
        paddingLeft: "0px",
      }}
    >
      <div
        style={{ marginLeft: "0px", marginRight: "1.5 rem", pointerEvents: sequence?.hasAnyUserVoted ? "none" : "auto" }}
        className="mb-6"
      >
        <h5 className="mb-4">Settings</h5>
        {sequence?.hasAnyUserVoted && (
          <div className="alert alert-warning mt-3">
            Editing the sequence structure is disabled because users voted for
            the sequence.
          </div>
        )}

        <div className="standard-dropdown mb-3">
          <div className="standard-dropdown-label">Type</div>
          <DropdownButton
            title={type}
            onSelect={(selectedType) => setType(selectedType)}
            style={dropdownStyle}
          >
            <Dropdown.Item eventKey="Survey">Survey</Dropdown.Item>
            <Dropdown.Item eventKey="Quiz">Quiz</Dropdown.Item>
          </DropdownButton>
        </div>

        {type === "Survey" && (
          <div className="standard-dropdown mb-3">
            <div className="standard-dropdown-label">Time to Next Poll</div>
            <DropdownButton
              title={timeToNextPoll}
              onSelect={(selectedTime) => setTimeToNextPoll(selectedTime)}
              style={dropdownStyle}
            >
              {timeToNextPollOptions.map((option) => (
                <Dropdown.Item key={option.id} eventKey={option.name}>
                  {option.name}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>
        )}

        {type === "Quiz" && (
          <>
            <div style={{ display: "flex", width: "100%", gap: "5px" }}>
              <div className="standard-dropdown mb-3">
                <div className="standard-dropdown-label">
                  Question Category(s)
                </div>
                <DropdownButton
                  title={
                    questionCategory.length > 0
                      ? questionCategory.join(", ")
                      : "Select Categories"
                  }
                  style={dropdownStyle}
                >
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <Dropdown.Item
                        key={category.id}
                        as="div"
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuestionCategory((prev) =>
                            prev.includes(category.text)
                              ? prev.filter((cat) => cat !== category.text)
                              : [...prev, category.text]
                          );
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={questionCategory.includes(category.text)}
                          onChange={() => {}}
                          style={{ marginRight: "10px" }}
                        />
                        {category.text}
                      </Dropdown.Item>
                    ))
                  ) : (
                    <Dropdown.Item disabled>
                      No categories available
                    </Dropdown.Item>
                  )}
                </DropdownButton>
              </div>
              {questionCategory.length > 0 &&
                numberOfQuestionsArray?.length > 0 && (
                  <div className="standard-dropdown mb-3">
                    <div className="standard-dropdown-label">
                      Number of Questions to show
                    </div>
                    <DropdownButton
                      onSelect={(selectedNumber) =>
                        setNumberOfQuestions(selectedNumber)
                      }
                      title={
                        numberOfQuestions > 0
                          ? numberOfQuestions
                          : "Select Number"
                      }
                      style={{ ...dropdownStyle, maxWidth: "unset" }}
                    >
                      {numberOfQuestionsArray.map((count) => (
                        <Dropdown.Item key={count} eventKey={count}>
                          {count}
                        </Dropdown.Item>
                      ))}
                    </DropdownButton>
                  </div>
                )}
            </div>

            <div className="standard-dropdown mb-3">
              <div className="standard-dropdown-label">Question Timer</div>
              <DropdownButton
                title={questionTimer}
                onSelect={(selectedTimer) => setQuestionTimer(selectedTimer)}
                style={dropdownStyle}
              >
                <Dropdown.Item eventKey="Off">Off</Dropdown.Item>
                <Dropdown.Item eventKey="5 seconds">5 seconds</Dropdown.Item>
                <Dropdown.Item eventKey="10 seconds">10 seconds</Dropdown.Item>
                <Dropdown.Item eventKey="15 seconds">15 seconds</Dropdown.Item>
                <Dropdown.Item eventKey="20 seconds">20 seconds</Dropdown.Item>
                <Dropdown.Item eventKey="30 seconds">30 seconds</Dropdown.Item>
              </DropdownButton>
            </div>

            <div className="standard-dropdown mb-3">
              <div className="standard-dropdown-label">Question Order</div>
              <DropdownButton
                title={questionOrder}
                onSelect={(selectedOrder) => setQuestionOrder(selectedOrder)}
                style={dropdownStyle}
              >
                <Dropdown.Item eventKey="In Order">In Order</Dropdown.Item>
                <Dropdown.Item eventKey="Random">Random</Dropdown.Item>
              </DropdownButton>
            </div>

            <div className="standard-dropdown mb-3">
              <div className="standard-dropdown-label">
                Show Correct Answers
              </div>
              <DropdownButton
                title={showCorrectAnswers}
                onSelect={(selectedOption) =>
                  setShowCorrectAnswers(selectedOption)
                }
                style={dropdownStyle}
              >
                <Dropdown.Item eventKey="Immediately">
                  Immediately
                </Dropdown.Item>
                <Dropdown.Item eventKey="End of Quiz">
                  End of Quiz
                </Dropdown.Item>
              </DropdownButton>
            </div>
          </>
        )}

        <div className="standard-dropdown mb-3">
          <div className="standard-dropdown-label">Advertising</div>
          <DropdownButton
            title={advertising}
            onSelect={(selectedAd) => setAdvertising(selectedAd)}
            style={dropdownStyle}
          >
            {advertisingOptions.map((option) => (
              <Dropdown.Item key={option.id} eventKey={option.name}>
                {option.name}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
      </div>
    </div>
  );
};

export default SequenceSettings;
